import { Injectable, Inject } from '@angular/core';
import { IpAddressService } from './ip-address.service';
import { NewRelicService } from './new-relic.service';
import { PARAM_TRANSFORMERS } from '../models';
import { LoggerConfig, LogLevel } from './logger-config';
import { LoggingEnvironment } from '../logging-environment.provider';
import { MegaBeaconService } from '../logger/mega-beacon.service';
import * as i0 from "@angular/core";
import * as i1 from "./new-relic.service";
import * as i2 from "./ip-address.service";
import * as i3 from "../logger/mega-beacon.service";
/**
 * Service to manage logging, both locally (to console) and to a server
 *
 * @export
 * @class LoggerService
 * @implements {OnDestroy}
 */
export class LoggerService {
    /**
     * Creates an instance of LoggerService.
     * @param {NewRelicService} newRelicService Injected
     * @memberof LoggerService
     */
    constructor(newRelicService, paramTransformers, ipService, environment, megaBeaconService) {
        this.newRelicService = newRelicService;
        this.paramTransformers = paramTransformers;
        this.ipService = ipService;
        this.environment = environment;
        this.megaBeaconService = megaBeaconService;
        /**
         * Use this to tag all log messages from this instance with the calling component/service/class
         *
         * @memberof LoggerService
         */
        this.caller = '';
    }
    /**
     * Logs the message and parameters to console (if level is at or above the set Local Logging Level)
     *
     * @protected
     * @param {LogLevel} level
     * @param {string} msg
     * @param {ActionParams} params
     * @returns
     * @memberof LoggerService
     */
    localLog(level, msg, params) {
        if (level < LoggerConfig.LocalLogLevel) {
            return;
        }
        const funcName = LogLevel[level].toLowerCase();
        const logParams = [];
        if (this.caller) {
            logParams.push(this.caller);
        }
        if (msg) {
            logParams.push(msg);
        }
        if (params) {
            logParams.push(params);
        }
        console[funcName](...logParams);
    }
    /**
     * Logs the message and parameters to teh server (New Relic) if level is at or above the set Local Logging Level
     *
     * @protected
     * @param {LogLevel} level
     * @param {string} msg
     * @param {ActionParams} params
     * @returns {void}
     * @memberof LoggerService
     */
    serverLog(level, msg, params) {
        if (level < LoggerConfig.ServerLogLevel) {
            return;
        }
        this.newRelicService.log(msg, this.caller, params);
    }
    get appVersion() {
        return this.environment.version ? this.environment.version : '';
    }
    /**
     * Updates a generic set of parameters to include otehr details, like who is logged in and the current time
     *
     * @protected
     * @param {{}} [params={}]
     * @returns {ActionParams}
     * @memberof LoggerService
     */
    updateParams(params = {}) {
        let updatedParams = params;
        updatedParams.timeStamp = Date.now();
        if (this.environment.version) {
            updatedParams.version = this.environment.version;
        }
        if (window.performance) {
            updatedParams.timeOffset = Math.round(window.performance.now());
        }
        updatedParams.ipAddress = this.ipService.ipAddress;
        this.paramTransformers
            .filter(transformer => transformer.initialized)
            .forEach(transformer => {
            updatedParams = {
                ...updatedParams,
                ...transformer.getParams()
            };
        });
        return updatedParams;
    }
    /**
     * Adds a message to the Log at the given Logging Level
     *
     * @param {LogLevel} level
     * @param {string} msg Message to log
     * @param {{}} params extra data to be logged (key/value pairs)
     * @param {boolean} [skipLocal=false] Option to only log to server
     * @memberof LoggerService
     */
    doLog(level, msg, params, skipLocal = false) {
        const updatedParams = this.updateParams(params);
        if (!skipLocal) {
            this.localLog(level, msg, updatedParams);
        }
        this.serverLog(level, msg, updatedParams);
    }
    doLogMegaBeacon(eventType, action, jsonSchemaFileName, additionalData, defaultApplicationWhenEmpty = '') {
        const params = {};
        const updatedParams = this.updateParams(params);
        if (!updatedParams.activeApplication) {
            updatedParams.activeApplication = defaultApplicationWhenEmpty;
        }
        return this.megaBeaconService
            .log(eventType, action, updatedParams, jsonSchemaFileName, additionalData)
            .pipe(response => response);
    }
    /**
     * Shortcut to log at LogLevel.LOG
     */
    log(msg, params = {}) {
        this.doLog(LogLevel.LOG, msg, params);
    }
    /**
     * Shortcut to log at LogLevel.DEBUG
     */
    debug(msg, params = {}) {
        this.doLog(LogLevel.DEBUG, msg, params);
    }
    /**
     * Shortcut to log at LogLevel.INFO
     */
    info(msg, params = {}) {
        this.doLog(LogLevel.INFO, msg, params);
    }
    /**
     * Shortcut to log at LogLevel.WARN
     */
    warn(msg, params = {}) {
        this.doLog(LogLevel.WARN, msg, params);
    }
    /**
     * Shortcut to log at LogLevel.ERROR
     */
    error(msg, params = {}) {
        this.doLog(LogLevel.ERROR, msg, params);
    }
    /**
     * Shortcut to log at LogLevel.ERROR
     */
    trace(msg, params = {}) {
        this.doLog(LogLevel.TRACE, msg, params);
    }
    /**
     * Gets a new instance of the logger, so the "caller" property is unique to that logger
     *
     * @param {string} caller
     * @returns {LoggerService}
     * @memberof LoggerService
     */
    instance(caller) {
        const loggerInstance = new LoggerService(this.newRelicService, this.paramTransformers, this.ipService, this.environment, this.megaBeaconService);
        loggerInstance.caller = caller;
        return loggerInstance;
    }
    /**
     * In order to work with parameters in New Relic (which does not support objects) this will
     * add a prefix to all properties of the given object
     *
     * @example
     * const logger = loggerService.instance('EntityLogger');
     * const entity = {id: '1234', name: 'my_entity', date: '2019/04/08'};
     * // Will create a log with additional properties "entityId", "entityName", "entityDate"
     * logger.log('Created Entity', {...logger.prefixProperties(entity, 'entity')});
     *
     * @param {{ [key: string]: any }} obj
     * @param {string} prefix
     * @param {PrefixStrategy} [prefixStrategy='camelCase']
     * @returns {{ [key: string]: any }}
     * @memberof LoggerService
     */
    prefixProperties(obj, prefix, prefixStrategy = 'camelCase') {
        if (prefixStrategy === 'underscore') {
            prefix += '_';
        }
        const prefixed = {};
        Object.keys(obj).forEach(key => {
            const newKey = prefix +
                (prefixStrategy !== 'camelCase' ? key : key.charAt(0).toUpperCase() + key.slice(1));
            prefixed[newKey] = obj[key];
        });
        return prefixed;
    }
    static { this.ɵfac = function LoggerService_Factory(__ngFactoryType__) { return new (__ngFactoryType__ || LoggerService)(i0.ɵɵinject(i1.NewRelicService), i0.ɵɵinject(PARAM_TRANSFORMERS), i0.ɵɵinject(i2.IpAddressService), i0.ɵɵinject(LoggingEnvironment), i0.ɵɵinject(i3.MegaBeaconService)); }; }
    static { this.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: LoggerService, factory: LoggerService.ɵfac }); }
}
//# sourceMappingURL=logger.service.js.map